import type { Fornecedor } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { EstoqueApi } from './estoque.api.js';

/** `<erp-fornecedor-form-modal>` — criação de fornecedor. Sem modo de edição — o backend não expõe `PATCH /fornecedores/:id` ainda (fora do escopo mínimo da Sprint 8). */
export class ErpFornecedorFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _estoqueApi: EstoqueApi | null = null;
  private _aoSalvar: ((fornecedor: Fornecedor) => void) | null = null;

  private aberto = false;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set estoqueApi(valor: EstoqueApi) {
    this._estoqueApi = valor;
  }

  set aoSalvar(valor: (fornecedor: Fornecedor) => void) {
    this._aoSalvar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrir(): void {
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._estoqueApi || this.enviando) return;

    const nome = this.valorCampo('#nome').trim();
    const telefone = this.valorCampo('#telefone').trim();
    const email = this.valorCampo('#email').trim();

    if (!nome) {
      this.mensagemErro = 'nome é obrigatório';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const fornecedor = await this._estoqueApi.criarFornecedor({
        nome,
        ...(telefone ? { telefone } : {}),
        ...(email ? { email } : {}),
      });
      this.aberto = false;
      this._aoSalvar?.(fornecedor);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Novo fornecedor</span>
        <form novalidate>
          <label>Nome <input id="nome" type="text" maxlength="255" /></label>
          <label>Telefone <input id="telefone" type="text" maxlength="30" /></label>
          <label>E-mail <input id="email" type="email" maxlength="255" /></label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-fornecedor-form-modal', ErpFornecedorFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-fornecedor-form-modal': ErpFornecedorFormModal;
  }
}
